import * as util from "../../../../common/util";
import { MonitorInfoDetail, adjustBoundsToBeOnMonitor, getAllMonitors, getMonitor } from "../../../../common/Monitors";
import { AuthenticationToken } from "../../../../clients/Interop/types";
import { LaunchGroupComponent, LaunchGroupStackedWindow, SpawnOptions } from "../types";
import uuidv4 from "uuid-random";
import { isNumber } from "lodash";

/**
 * This method takes the main window position params and checks whether they are specified in percentage or pixels.
 * If positioning is specified in percentage grabs the screen dimensions to translate the positioning in percentage to pixels.
 *
 * NB - There is an assumption that if there is at least one parameter specified in percentage then it assumes all values are percentages,
 * if that is not the case the configuration will fail to process and an exception will be thrown.
 *
 * @param windowPositions The main launchGroup component window positions.
 * @returns {object} The processed window positions.
 */
async function parseLaunchGroupWindowPosition(windowPositions) {
	let isPercentage = false;
	for (const key of Object.keys(windowPositions)) {
		if (util.isPercentage(windowPositions[key])) {
			isPercentage = true;
			break;
		}
	}

	// search the monitor for window
	// the parent window monitor is default
	let requiredMonitor: MonitorInfoDetail;
	const monitors = await getAllMonitors();
	if (isNumber(windowPositions.monitor)) {
		requiredMonitor =
			monitors.find((x) => parseInt(x.name.replace("device", "")) == windowPositions.monitor) ?? (await getMonitor());
	} else {
		requiredMonitor = await getMonitor();
	}

	if (isPercentage) {
		const monitorDimensions = requiredMonitor.availableRect;

		const calculateValueInPixelsForDimension = (dimension, monitorDimensionSize) => {
			if (util.isPercentage(dimension)) {
				return (monitorDimensionSize * parseFloat(dimension)) / 100;
			} else {
				return parseFloat(dimension);
			}
		};

		// height and width should always been passed
		const width = calculateValueInPixelsForDimension(windowPositions.width, monitorDimensions.width);
		const height = calculateValueInPixelsForDimension(windowPositions.height, monitorDimensions.height);

		const requiredTop = calculateValueInPixelsForDimension(windowPositions.top, monitorDimensions.height);
		const requiredBottom = calculateValueInPixelsForDimension(windowPositions.bottom, monitorDimensions.height);
		const requiredLeft = calculateValueInPixelsForDimension(windowPositions.left, monitorDimensions.width);
		const requiredRight = calculateValueInPixelsForDimension(windowPositions.right, monitorDimensions.width);

		// calculation of the top and the left positions depending on which parameter (top || bottom OR left || right) is passed
		const top = isNaN(requiredBottom) ? requiredTop : monitorDimensions.height - height - requiredBottom;
		const left = isNaN(requiredRight) ? requiredLeft : monitorDimensions.width - width - requiredRight;

		const right = left + width;
		const bottom = top + height;

		const adjustedBounds = adjustBoundsToBeOnMonitor({ allMonitors: monitors } as any, {
			top,
			left,
			right,
			bottom,
			width,
			height,
		});
		return {
			top: adjustedBounds.top,
			left: adjustedBounds.left,
			right: adjustedBounds.right,
			bottom: adjustedBounds.bottom,
			width: adjustedBounds.width,
			height: adjustedBounds.height,
		};
	} else {
		return windowPositions;
	}
}

function checkIfCanCalculateComponentPositionWithinGroup(
	componentsToSpawn: (LaunchGroupComponent | LaunchGroupStackedWindow)[],
	windowParams: any
) {
	if (windowParams.right == undefined) {
		//check we if we have any components positioned using right
		componentsToSpawn.forEach((element) => {
			if (element.spawnOptions.right != undefined) {
				throw new Error(
					`Unable to calculate position of component within group as the group right coordinate on the monitor was not specified or spawn options for the component were incomplete, groupParams: \n${JSON.stringify(
						windowParams,
						null,
						2
					)}\nproblem component:\n${JSON.stringify(element, null, 2)}`
				);
			}
		});
	}
}

function validateLaunchGroupInnerComponentsPosition(
	componentsToSpawn: (LaunchGroupComponent | LaunchGroupStackedWindow)[],
	windowParams: any
) {
	if (!(windowParams.width && windowParams.height)) {
		//check we if we have any % based component sizes/positions
		componentsToSpawn.forEach((element) => {
			if (
				isNaN(element.spawnOptions.top) ||
				isNaN(element.spawnOptions.left) ||
				isNaN(element.spawnOptions.width) ||
				isNaN(element.spawnOptions.height)
			) {
				throw new Error(
					`Unable to calculate position of component within group as the group width or height was not specified or spawn options for the component were incomplete, groupParams: \n${JSON.stringify(
						windowParams,
						null,
						2
					)}\nproblem component:\n${JSON.stringify(element, null, 2)}`
				);
			}
		});
	}
}

function validateLaunchGroupComponentsConfigs(
	componentsToSpawn: (LaunchGroupComponent | LaunchGroupStackedWindow)[],
	windowParams: any
) {
	//check all components have position and size info
	componentsToSpawn.forEach((element) => {
		if (
			(element.spawnOptions.top == undefined && element.spawnOptions.bottom == undefined) ||
			(!element.spawnOptions.left == undefined && element.spawnOptions.right == undefined) ||
			(!element.spawnOptions.width &&
				(element.spawnOptions.left == undefined || element.spawnOptions.right == undefined)) ||
			(!element.spawnOptions.height &&
				(element.spawnOptions.top == undefined || element.spawnOptions.bottom == undefined))
		) {
			throw new Error(
				`Incomplete config for component within group (a position and size must be derivable from the config), groupParams provided: \n${JSON.stringify(
					windowParams,
					null,
					2
				)}\nproblem component:\n${JSON.stringify(element, null, 2)}`
			);
		}
	});

	checkIfCanCalculateComponentPositionWithinGroup(componentsToSpawn, windowParams);
	validateLaunchGroupInnerComponentsPosition(componentsToSpawn, windowParams);
}

function validateLaunchGroupDimensionsAndPositions(windowParams) {
	if (
		(windowParams.top != undefined && isNaN(windowParams.top)) ||
		(windowParams.left != undefined && isNaN(windowParams.left)) ||
		(windowParams.bottom != undefined && isNaN(windowParams.bottom)) ||
		(windowParams.right != undefined && isNaN(windowParams.right)) ||
		(windowParams.width != undefined && isNaN(windowParams.width)) ||
		(windowParams.height != undefined && isNaN(windowParams.height))
	) {
		throw new Error(
			`Group dimensions and positions must be specified in pixels relative to the current monitor, problem component: \n${JSON.stringify(
				windowParams,
				null,
				2
			)}`
		);
	}
}

/**
 * Process the launchGroup component configuration by making the necessary positioning calculations
 * and adding the correct spawn parameters in each component to spawn.
 *
 * @param {Object} launchGroupComponentConfig The launchGroup component configuration object to be processed.
 * @returns {Object} An object of the processed configuration with the additional launchGroup component data.
 */
const processLaunchGroupComponentConfiguration = async (config) => {
	const clonedConfig = JSON.parse(JSON.stringify(config));
	const componentsToSpawn: (LaunchGroupComponent | LaunchGroupStackedWindow)[] = JSON.parse(
		JSON.stringify(clonedConfig.window.toSpawn)
	);
	const windowParams = clonedConfig.window;
	const addToWorkspace = windowParams.addToWorkspace ?? true;
	const launchGroupComponentUUID = uuidv4();

	const launchGroupWindowParams = {
		top: windowParams.top ?? 0,
		left: windowParams.left ?? 0,
		bottom: windowParams.bottom,
		right: windowParams.right,
		width: windowParams.width,
		height: windowParams.height,
		monitor: windowParams.monitor,
	};

	const launchGroupWindowPositions = await parseLaunchGroupWindowPosition(launchGroupWindowParams);

	// Object.assign mutate the target (windowParams in this case) hence no left hand assignment made
	Object.assign(windowParams, launchGroupWindowPositions);

	validateLaunchGroupDimensionsAndPositions(windowParams);

	validateLaunchGroupComponentsConfigs(componentsToSpawn, windowParams);

	const _processCoordinateOrDimension = function (value, groupBasisCoordinate, groupBasisDimension, roundingFn) {
		if (value != undefined) {
			if (util.isPercentage(value)) {
				value = roundingFn((parseFloat(value) / 100.0) * groupBasisDimension);
			}
			return groupBasisCoordinate + value;
		} else {
			return undefined;
		}
	};

	//process the array of components to set position arguments
	const processedComponents = componentsToSpawn.map((component) => {
		//process spawnOptions, convert any % based args into pixel coordinates, offset by group position
		//  N.B. Rounding of coordinates is awkward and must be done correctly to avoid overlaps...
		component.spawnOptions.top = _processCoordinateOrDimension(
			component.spawnOptions.top,
			windowParams.top,
			windowParams.height,
			Math.round
		);
		component.spawnOptions.left = _processCoordinateOrDimension(
			component.spawnOptions.left,
			windowParams.left,
			windowParams.width,
			Math.round
		);

		//interpret bottom and right as spawn does - distance from the bottom and right edges
		component.spawnOptions.bottom = _processCoordinateOrDimension(
			component.spawnOptions.bottom,
			windowParams.bottom,
			windowParams.height,
			Math.round
		);
		component.spawnOptions.right = _processCoordinateOrDimension(
			component.spawnOptions.right,
			windowParams.right,
			windowParams.width,
			Math.round
		);

		component.spawnOptions.height = _processCoordinateOrDimension(
			component.spawnOptions.height,
			0,
			windowParams.height,
			Math.round
		);
		component.spawnOptions.width = _processCoordinateOrDimension(
			component.spawnOptions.width,
			0,
			windowParams.width,
			Math.round
		);

		//base positioning on absolute monitor coordinates (rather than available space)
		component.spawnOptions.position = "available";

		//set the chosen group monitor for individual window or primary monitor as default if the monitor is not provided
		component.spawnOptions.monitor = windowParams.monitor ?? 0;

		//ensure the spawned components are added to the workspace (if they are supposed to be)
		component.spawnOptions.addToWorkspace = addToWorkspace;

		if (windowParams.linkerGroup) {
			if (!component.spawnOptions.data) {
				component.spawnOptions.data = {};
			}
			component.spawnOptions.data.linker = { channels: [windowParams.linkerGroup] };
		}

		component.launchGroupComponentData = {
			launchGroupComponentUUID: launchGroupComponentUUID,
			closeComponentsTogether: windowParams.closeComponentsTogether || false,
		};

		return component;
	});

	return {
		groupParams: windowParams,
		componentsToSpawn: processedComponents,
		launcherGroupComponentType: clonedConfig.component.type,
	};
};

/**
 * Generate dynamic authentication token.
 * The InteropService will need this token to authenticate new app clients.
 * Any window being spawned that will use an FDC3 client will also need this authentication token for registration -- the token is preloaded into new windows custom data.
 * Any unused authentication tokens will be discarded by Interop resolver when stale (i.e.TTL expires).
 *
 * @param {string} componentName the app/component name to generate the token for
 * @param {SpawnOptions} options window options to update
 * @returns {SpawnOptions} the updated window options, whose custom data property now contains the newly generated authentication token (this will be picked up in app by FDC3 client)
 */
const generateDynamicAuthenticationToken = (windowName: string, componentName: string): AuthenticationToken => {
	// create an authentication token for the app being spawned (only used for fdc3 compliant components)
	const authenticationToken: AuthenticationToken = {
		signatureStringType: "dynamic",
		// The window name in the signatureString is what authenticates the app; everything else in the token is information
		signatureString: windowName,
		meta: { name: componentName, appId: componentName },
		timestamp: Date.now(),
	};
	return authenticationToken;
};

/**
 * Adds authentication toke to the spawn options's custom data that is passed to new window.
 *
 * @param options window spawn options
 * @param authenticationToken dynamic authentication token that match one sent to InteropService
 *
 * @returns updated spawn options
 */
const updateCustomDataWithAuthToken = (
	options: SpawnOptions | undefined,
	authenticationToken: AuthenticationToken
): SpawnOptions => {
	options = options ?? {};
	options.customData = options.customData ?? {};
	// pass the authentication token to spawned app/component to include in fdc3 client registration (if it matches InteropService's version then authentication is successful)
	options.customData.authenticationToken = authenticationToken;
	return options;
};

export { processLaunchGroupComponentConfiguration, generateDynamicAuthenticationToken, updateCustomDataWithAuthToken };
